import { TableRow } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import { memo } from "react";
import CustomTable from "../../../../shared/components/CustomTable";
import { StyledTableCell } from "../../../../shared/components/CustomTable/Styled";
import IconButton from "../../../../shared/components/IconButton";
import SearchBarWithSuggestion from "../../../../shared/components/SearchBar/SearchBarWithSuggestion";
import TextBox from "../../../../shared/components/TextBox";
import { api } from "../../../../shared/hooks/api";
import { color } from "../../../../shared/styles";
import DeleteIcon from "../../../../shared/svg/DeleteIcon";
import { formatInternationalNumber, parseInternationalToNumber } from "../../../../utils/format";
import { buildInventoryModalSearchParams } from "../../../../utils/inventoryModalSearchParams";
import { error } from "../../../../utils/toast";

interface ImportMaterialListProps {
    selectedMaterials: any[];
    setSelectedMaterials: Function;
}

interface MaterialRowProps {
    item: any;
    extraRowProps: any;
}

const DUE_DATE_COLUMN_WIDTH = "180px";
const DUE_DATE_PICKER_WIDTH = "156px";

const dueDatePickerSx = {
    width: DUE_DATE_PICKER_WIDTH,
    minWidth: DUE_DATE_PICKER_WIDTH,
    "& .MuiInputBase-root": {
        boxSizing: "border-box",
        width: "100%",
        height: "36px",
        fontSize: "13px",
        fontStyle: "normal",
        fontWeight: 400,
        lineHeight: "normal",
        fontFamily: "Noto Sans",
        paddingRight: "4px",
        overflow: "hidden",
    },
    "& .MuiInputBase-input": {
        flex: "1 1 auto",
        minWidth: 0,
        padding: "7px 0 7px 10px",
        height: "22px",
        boxSizing: "border-box",
        "&::placeholder": {
            color: "#757575",
            opacity: 1,
        },
    },
    "& .MuiInputAdornment-root": {
        flexShrink: 0,
        marginLeft: "2px",
        height: "100%",
        maxHeight: "none",
    },
    "& .MuiIconButton-root": {
        marginRight: 0,
        padding: "3px",
    },
    "& .MuiSvgIcon-root": {
        fontSize: "18px",
    },
    "& .MuiOutlinedInput-root": {
        "& fieldset": {
            border: `1px solid ${color.emphasis.NEUTRAL_N90}`,
            borderRadius: "5px",
        },
        "&:hover fieldset": {
            border: `1px solid ${color.emphasis.NEUTRAL_N90}`,
        },
        "&.Mui-focused fieldset": {
            border: `1px solid ${color.emphasis.NEUTRAL_N90}`,
        },
    },
};

export const TableColumns = [
    { text: "Vật tư", icon: null },
    { text: "Đơn vị", icon: null },
    { text: "Số lượng nhập", icon: null },
    { text: "Ngày đến hạn", icon: null, width: DUE_DATE_COLUMN_WIDTH },
    { text: "Ghi chú", icon: null },
    { text: "", icon: null },
];

const MaterialRow = memo(({ item, extraRowProps }: MaterialRowProps) => {
    return (
        <TableRow>
            <StyledTableCell>{item.name}</StyledTableCell>
            <StyledTableCell>{item.measurement_unit}</StyledTableCell>
            <StyledTableCell>
                <TextBox
                    style={{ width: "80px" }}
                    text={formatInternationalNumber(item.importQuantity ?? "")}
                    onChangeText={(value: string) => {
                        const n = parseInternationalToNumber(value, error);
                        if (!Number.isFinite(n)) return;
                        extraRowProps.onChangeImportQuantity(item.id, n);
                    }}
                />
            </StyledTableCell>
            <StyledTableCell align="left">
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="vi">
                    <DatePicker
                        value={item.import_date ? dayjs(item.import_date) : null}
                        onChange={(newValue) => {
                            extraRowProps.onChangeImportDate(item.id, newValue ? newValue.format("YYYY-MM-DD") : null);
                        }}
                        format="DD/MM/YYYY"
                        slotProps={{
                            textField: {
                                placeholder: "DD/MM/YYYY",
                            },
                            popper: {
                                placement: "bottom-end",
                                sx: {
                                    "& .MuiPaper-root": {
                                        marginTop: "4px",
                                    },
                                    "& .MuiPickersDay-root.Mui-selected": {
                                        backgroundColor: "#0058A0",
                                        "&:hover": {
                                            backgroundColor: "#0058A0",
                                        },
                                    },
                                    "& .MuiDayCalendar-weekDayLabel": {
                                        color: "#333739",
                                    },
                                },
                            },
                        }}
                        dayOfWeekFormatter={(day) => {
                            const weekdays = ["CN", "T2", "T3", "T4", "T5", "T6", "T7"];
                            return weekdays[day.day()];
                        }}
                        sx={dueDatePickerSx}
                    />
                </LocalizationProvider>
            </StyledTableCell>
            <StyledTableCell>
                <TextBox
                    text={item.note || ""}
                    onChangeText={(value: string) => extraRowProps.onChangeNote(item.id, value)}
                    style={{ width: "150px" }}
                />
            </StyledTableCell>
            <StyledTableCell>
                <IconButton onClick={() => extraRowProps.removeSelectedItem(item.id)}>
                    <DeleteIcon />
                </IconButton>
            </StyledTableCell>
        </TableRow>
    );
});

MaterialRow.displayName = "MaterialRow";

export const ImportMaterialList = ({ selectedMaterials, setSelectedMaterials }: ImportMaterialListProps) => {
    const [{ data }, fetchMaterials] = api.get(
        "/materials/",
        buildInventoryModalSearchParams(),
        true,
    );

    const materials = data && data.results ? data.results : [];

    const fetchItems = (variables: any) => {
        fetchMaterials(buildInventoryModalSearchParams(variables), true);
    };

    const handleOnclickItem = (item: any, checked: boolean) => {
        let updatedMaterials;
        if (checked) {
            if (!selectedMaterials.find((material) => material.id === item.id)) {
                updatedMaterials = [...selectedMaterials, { ...item, importQuantity: 0, note: "" }];
            } else {
                updatedMaterials = [...selectedMaterials];
            }
        } else {
            updatedMaterials = selectedMaterials.filter((material) => material.id !== item.id);
        }
        setSelectedMaterials(updatedMaterials);
    };

    const onChangeImportQuantity = (id: string, quantity: number) => {
        const updatedMaterials = selectedMaterials.map((material: any) =>
            material.id === id ? { ...material, importQuantity: quantity } : material,
        );
        setSelectedMaterials(updatedMaterials);
    };

    const onChangeImportDate = (id: string, date: string) => {
        const updatedMaterials = selectedMaterials.map((material: any) =>
            material.id === id ? { ...material, import_date: date } : material,
        );
        setSelectedMaterials(updatedMaterials);
    };

    const onChangeNote = (id: string, note: string) => {
        const updatedMaterials = selectedMaterials.map((material: any) =>
            material.id === id ? { ...material, note } : material,
        );
        setSelectedMaterials(updatedMaterials);
    };

    const removeSelectedItem = (id: string) => {
        const updatedMaterials = selectedMaterials.filter((selectedItem: any) => selectedItem.id !== id);
        setSelectedMaterials(updatedMaterials);
    };

    return (
        <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 20 }}>
            <SearchBarWithSuggestion
                menuList={materials}
                selectedItems={selectedMaterials}
                placeholder="Tìm kiếm vật tư"
                fetchItems={fetchItems}
                handleOnclickItem={handleOnclickItem}
            />
            <CustomTable
                itemList={selectedMaterials}
                TableColumns={TableColumns}
                RowElement={MaterialRow}
                extraRowProps={{
                    onChangeImportQuantity,
                    onChangeImportDate,
                    onChangeNote,
                    removeSelectedItem,
                }}
                containerStyle={{ maxHeight: "400px" }}
            />
        </div>
    );
};
